/* =============================================
   THEME.JS — Skate Society
   ============================================= */

/* Header — sticky scroll shadow + mobile menu
   ============================================= */
const HeaderBehavior = (() => {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 10);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

/* Mobile Navigation
   ============================================= */
class MobileNav {
  #nav;
  #overlay;
  #toggleBtn;
  #closeBtn;

  constructor() {
    this.#nav = document.getElementById('MobileNav');
    this.#overlay = document.getElementById('CartOverlay');
    this.#toggleBtn = document.querySelector('[data-mobile-nav-toggle]');
    this.#closeBtn = document.querySelector('[data-mobile-nav-close]');

    if (!this.#nav || !this.#toggleBtn) return;
    this.#init();
  }

  #init() {
    this.#toggleBtn.addEventListener('click', () => this.toggle());
    this.#closeBtn?.addEventListener('click', () => this.close());
    this.#overlay?.addEventListener('click', () => this.close());
  }

  open() {
    this.#nav.classList.add('is-open');
    this.#toggleBtn.classList.add('is-active');
    this.#overlay?.classList.add('is-visible');
    document.body.classList.add('drawer-open');
  }

  close() {
    this.#nav.classList.remove('is-open');
    this.#toggleBtn.classList.remove('is-active');
    document.body.classList.remove('drawer-open');
    if (!window.cartDrawer?.isOpen) {
      this.#overlay?.classList.remove('is-visible');
    }
  }

  toggle() {
    this.#nav.classList.contains('is-open') ? this.close() : this.open();
  }
}

/* Cart Drawer
   ============================================= */
class CartDrawer {
  #drawer;
  #overlay;
  isOpen = false;

  constructor() {
    this.#drawer = document.getElementById('CartDrawer');
    this.#overlay = document.getElementById('CartOverlay');
    if (!this.#drawer) return;
    this.#init();
  }

  #init() {
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', () => this.toggle());
    });

    this.#drawer.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
    this.#overlay?.addEventListener('click', () => this.close());

    document.addEventListener('cart:open', () => this.open());
  }

  async open() {
    this.isOpen = true;
    this.#drawer.classList.add('is-open');
    this.#overlay?.classList.add('is-visible');
    document.body.classList.add('drawer-open');
    await this.#refresh();
  }

  close() {
    this.isOpen = false;
    this.#drawer.classList.remove('is-open');
    this.#overlay?.classList.remove('is-visible');
    document.body.classList.remove('drawer-open');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  async #refresh() {
    try {
      const res = await fetch(window.cartRoutes.cart);
      const cart = await res.json();
      this.#render(cart);
    } catch (e) {
      console.error('Cart fetch failed', e);
    }
  }

  #render(cart) {
    this.#updateCount(cart.item_count);

    const body = this.#drawer.querySelector('.cart-drawer__body');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg viewBox="0 0 24 24"><path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"/></svg>
          <p class="cart-drawer__empty-title">Your cart is empty</p>
          <p>Looks like you haven't added anything yet.</p>
          <a href="/collections/all" class="btn btn--primary btn--sm">Shop Now</a>
        </div>`;
    } else {
      const items = cart.items.map(item => this.#renderItem(item)).join('');
      body.innerHTML = `<ul class="cart-items">${items}</ul>`;
      body.querySelectorAll('[data-qty-change]').forEach(btn => {
        btn.addEventListener('click', e => this.#changeQty(e));
      });
    }

    const subtotal = this.#drawer.querySelector('.cart-drawer__subtotal-amount');
    if (subtotal) subtotal.textContent = this.#formatMoney(cart.total_price);

    const countEl = this.#drawer.querySelector('.cart-drawer__count');
    if (countEl) countEl.textContent = cart.item_count;
  }

  #renderItem(item) {
    return `
      <li class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          <img src="${item.featured_image?.url || ''}" alt="${item.product_title}" loading="lazy">
        </div>
        <div class="cart-item__info">
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <p class="cart-item__price">${this.#formatMoney(item.final_line_price)}</p>
          <div class="cart-item__footer">
            <div class="qty-control">
              <button class="qty-control__btn" data-qty-change data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease">−</button>
              <span class="qty-control__input">${item.quantity}</span>
              <button class="qty-control__btn" data-qty-change data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase">+</button>
            </div>
            <button class="cart-item__remove" data-qty-change data-key="${item.key}" data-qty="0">Remove</button>
          </div>
        </div>
      </li>`;
  }

  async #changeQty(e) {
    const btn = e.currentTarget;
    const key = btn.dataset.key;
    const qty = parseInt(btn.dataset.qty, 10);

    try {
      const res = await fetch(window.cartRoutes.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      const cart = await res.json();
      this.#render(cart);
    } catch (e) {
      console.error('Qty change failed', e);
    }
  }

  #updateCount(count) {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count > 0 ? count : '';
      el.dataset.count = count;
    });
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return window.shopMoneyFormat?.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100)) || `$${amount}`;
  }
}

/* Add To Cart
   ============================================= */
const initAddToCart = () => {
  document.querySelectorAll('[data-add-to-cart]').forEach(form => {
    if (form.dataset.addToCartInit) return;
    form.dataset.addToCartInit = '1';

    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn = form.querySelector('[data-add-to-cart-btn]');
      if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

      try {
        const res = await fetch(window.cartRoutes.add, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        });

        if (res.ok) {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      } catch (err) {
        console.error('Add to cart failed', err);
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = 'Add to Cart'; }
      }
    });
  });
};

/* FAQ Accordion
   ============================================= */
const initFaq = () => {
  document.querySelectorAll('.faq__question').forEach(question => {
    question.addEventListener('click', () => {
      const item = question.closest('.faq__item');
      const answer = item.querySelector('.faq__answer');
      const isOpen = item.classList.contains('is-open');

      document.querySelectorAll('.faq__item.is-open').forEach(openItem => {
        openItem.classList.remove('is-open');
        openItem.querySelector('.faq__answer').style.maxHeight = '0';
      });

      if (!isOpen) {
        item.classList.add('is-open');
        answer.style.maxHeight = answer.scrollHeight + 'px';
      }
    });
  });
};

/* Currency Selector — auto-submit on change
   ============================================= */
const initCurrencySelector = () => {
  document.querySelectorAll('.currency-selector__select').forEach(select => {
    select.addEventListener('change', () => select.closest('form')?.submit());
  });
};

/* Money formatter helper
   ============================================= */
const formatMoney = (cents) => {
  const amount = (cents / 100).toFixed(2);
  return (window.shopMoneyFormat || '${{amount}}')
    .replace('{{amount}}', amount)
    .replace('{{amount_no_decimals}}', Math.round(cents / 100));
};

/* Hero Slider — auto-play, dots, arrows, swipe
   ============================================= */
class HeroSlider {
  #el;
  #track;
  #slides;
  #dots;
  #current = 0;
  #autoplay;
  #interval;
  #timer = null;

  constructor(el) {
    this.#el = el;
    this.#track  = el.querySelector('[data-hero-slider-track]');
    this.#slides = [...el.querySelectorAll('[data-hero-slider-slide]')];
    this.#dots   = [...el.querySelectorAll('[data-hero-slider-dot]')];
    this.#autoplay = el.dataset.autoplay === 'true';
    this.#interval = parseInt(el.dataset.interval, 10) || 6000;

    if (this.#slides.length <= 1) return;
    this.#init();
  }

  #init() {
    this.#el.querySelector('[data-hero-slider-prev]')?.addEventListener('click', () => this.prev());
    this.#el.querySelector('[data-hero-slider-next]')?.addEventListener('click', () => this.next());

    for (const dot of this.#dots) {
      dot.addEventListener('click', () => this.goto(parseInt(dot.dataset.heroSliderDot, 10)));
    }

    if (this.#autoplay) {
      this.#start();
      this.#el.addEventListener('mouseenter', () => this.#stop());
      this.#el.addEventListener('mouseleave', () => this.#start());
    }

    // Touch swipe
    let startX = 0;
    this.#el.addEventListener('touchstart', e => { startX = e.touches[0].clientX; this.#stop(); }, { passive: true });
    this.#el.addEventListener('touchend',   e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) diff > 0 ? this.next() : this.prev();
      if (this.#autoplay) this.#start();
    }, { passive: true });

    // Keyboard
    this.#el.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

  goto(index) {
    const len = this.#slides.length;
    this.#current = ((index % len) + len) % len;
    this.#track.style.transform = `translateX(-${this.#current * 100}%)`;
    for (let i = 0; i < this.#dots.length; i++) {
      const isActive = i === this.#current;
      this.#dots[i].classList.toggle('is-active', isActive);
      this.#dots[i].setAttribute('aria-selected', isActive);
    }
  }

  next() { this.goto(this.#current + 1); this.#restart(); }
  prev() { this.goto(this.#current - 1); this.#restart(); }

  #start() { if (!this.#autoplay) return; this.#stop(); this.#timer = setInterval(() => this.next(), this.#interval); }
  #stop()  { if (this.#timer) { clearInterval(this.#timer); this.#timer = null; } }
  #restart() { if (this.#autoplay) this.#start(); }
}

/* Product Slider — scroll-snap with arrow nav
   ============================================= */
class ProductSlider {
  #el;
  #track;

  constructor(el) {
    this.#el = el;
    this.#track = el.querySelector('[data-product-slider-track]');
    if (!this.#track) return;

    const root = el.closest('.shop-category, .product-slider-wrapper') || el.parentElement;
    root?.querySelector('[data-product-slider-prev]')?.addEventListener('click', () => this.scroll(-1));
    root?.querySelector('[data-product-slider-next]')?.addEventListener('click', () => this.scroll(1));
  }

  scroll(direction) {
    const slide = this.#track.querySelector('.product-slider__slide');
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.#track).gap) || 16;
    const distance = (slide.offsetWidth + gap) * direction;
    this.#track.scrollBy({ left: distance, behavior: 'smooth' });
  }
}

/* Search Popup — predictive search with Shopify suggest API
   ============================================= */
class SearchPopup {
  #popup;
  #input;
  #results;
  #suggestions;
  #debounceTimer;

  constructor() {
    this.#popup = document.getElementById('SearchPopup');
    if (!this.#popup) return;

    this.#input       = this.#popup.querySelector('[data-search-input]');
    this.#results     = this.#popup.querySelector('[data-search-results]');
    this.#suggestions = this.#popup.querySelector('[data-search-suggestions]');
    this.#init();
  }

  #init() {
    document.querySelectorAll('[data-search-toggle]').forEach(btn => {
      btn.addEventListener('click', e => { e.preventDefault(); this.open(); });
    });

    this.#popup.querySelectorAll('[data-search-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    this.#input?.addEventListener('input', () => {
      clearTimeout(this.#debounceTimer);
      const q = this.#input.value.trim();
      if (q.length < 2) {
        this.#results.innerHTML = '';
        this.#popup.classList.remove('is-searching');
        return;
      }
      this.#debounceTimer = setTimeout(() => this.#search(q), 250);
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.#popup.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.#popup.classList.add('is-open');
    this.#popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-open');
    setTimeout(() => this.#input?.focus(), 80);
  }

  close() {
    this.#popup.classList.remove('is-open', 'is-searching');
    this.#popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-open');
    if (this.#input) this.#input.value = '';
    this.#results.innerHTML = '';
  }

  async #search(query) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=5&resources[options][unavailable_products]=last`;
      const res = await fetch(url);
      const data = await res.json();
      this.#render(data?.resources?.results || {}, query);
    } catch (e) {
      console.error('Search failed', e);
    }
  }

  #render(results, query) {
    this.#popup.classList.add('is-searching');
    const products    = results.products    || [];
    const collections = results.collections || [];
    const articles    = results.articles    || [];

    if (!products.length && !collections.length && !articles.length) {
      this.#results.innerHTML = `<p class="search-popup__empty">No results for "${this.#esc(query)}"</p>`;
      return;
    }

    const sections = [];

    if (products.length) {
      sections.push(`
        <div class="search-result-group">
          <h4 class="search-result-group__heading">Products</h4>
          <ul class="search-result-list">
            ${products.map(p => `
              <li>
                <a href="${p.url}" class="search-result-item">
                  <div class="search-result-item__image">
                    ${p.image ? `<img src="${p.image}" alt="${this.#esc(p.title)}" loading="lazy">` : ''}
                  </div>
                  <div class="search-result-item__info">
                    <p class="search-result-item__title">${this.#esc(p.title)}</p>
                    <p class="search-result-item__price">${p.price || ''}</p>
                  </div>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>
      `);
    }

    if (collections.length) {
      sections.push(`
        <div class="search-result-group">
          <h4 class="search-result-group__heading">Collections</h4>
          <ul class="search-result-list">
            ${collections.map(c => `<li><a href="${c.url}" class="search-result-link">${this.#esc(c.title)}</a></li>`).join('')}
          </ul>
        </div>
      `);
    }

    if (articles.length) {
      sections.push(`
        <div class="search-result-group">
          <h4 class="search-result-group__heading">Articles</h4>
          <ul class="search-result-list">
            ${articles.map(a => `<li><a href="${a.url}" class="search-result-link">${this.#esc(a.title)}</a></li>`).join('')}
          </ul>
        </div>
      `);
    }

    sections.push(`<a href="/search?q=${encodeURIComponent(query)}" class="search-popup__view-all">View all results</a>`);
    this.#results.innerHTML = sections.join('');
  }

  #esc(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

/* Quick View Drawer — fetch product JSON and render
   ============================================= */
class QuickView {
  #drawer;
  #body;
  #overlay;
  #currentProduct = null;
  isOpen = false;

  constructor() {
    this.#drawer  = document.getElementById('QuickViewDrawer');
    this.#body    = document.getElementById('QuickViewBody');
    this.#overlay = document.getElementById('CartOverlay');
    if (!this.#drawer) return;
    this.#init();
  }

  #init() {
    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-quick-view]');
      if (!trigger) return;
      e.preventDefault();
      const handle = trigger.dataset.productHandle;
      if (handle) this.open(handle);
    });

    this.#drawer.querySelector('[data-quick-view-close]')?.addEventListener('click', () => this.close());
    this.#overlay?.addEventListener('click', () => { if (this.isOpen) this.close(); });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  async open(handle) {
    this.isOpen = true;
    this.#drawer.classList.add('is-open');
    this.#drawer.setAttribute('aria-hidden', 'false');
    this.#overlay?.classList.add('is-visible');
    document.body.classList.add('drawer-open');
    this.#body.innerHTML = `<div class="quick-view-drawer__loading"><div class="spinner"></div></div>`;

    try {
      const res = await fetch(`/products/${handle}.js`);
      const product = await res.json();
      this.#currentProduct = product;
      this.#render(product);
    } catch (e) {
      this.#body.innerHTML = `<p style="text-align:center;padding:2rem;color:var(--color-text-muted)">Failed to load product</p>`;
    }
  }

  close() {
    this.isOpen = false;
    this.#drawer.classList.remove('is-open');
    this.#drawer.setAttribute('aria-hidden', 'true');
    if (!window.cartDrawer?.isOpen) {
      this.#overlay?.classList.remove('is-visible');
      document.body.classList.remove('drawer-open');
    }
  }

  #render(product) {
    const onSale  = product.compare_at_price > product.price;
    const discount = onSale ? Math.round((product.compare_at_price - product.price) / product.compare_at_price * 100) : 0;
    const firstVariant = product.variants[0];

    const variantsHtml = product.variants.length > 1
      ? this.#renderVariants(product)
      : `<input type="hidden" name="id" value="${firstVariant.id}">`;

    this.#body.innerHTML = `
      <div class="qv-product">
        <div class="qv-product__media">
          ${onSale ? `<div class="product-card__badges"><span class="product-card__badge product-card__badge--sale">-${discount}%</span></div>` : ''}
          <img src="${product.featured_image || ''}" alt="${this.#esc(product.title)}" class="qv-product__image">
        </div>
        <div class="qv-product__info">
          ${product.vendor ? `<p class="qv-product__vendor">${this.#esc(product.vendor)}</p>` : ''}
          <h3 class="qv-product__title">${this.#esc(product.title)}</h3>
          <div class="qv-product__price">
            ${onSale
              ? `<span class="qv-product__price--sale">${formatMoney(product.price)}</span>
                 <span class="qv-product__price--compare">${formatMoney(product.compare_at_price)}</span>`
              : `<span>${formatMoney(product.price)}</span>`}
          </div>
          ${product.description ? `<div class="qv-product__description">${this.#truncate(product.description, 240)}</div>` : ''}

          <form data-add-to-cart class="qv-product__form" data-qv-form>
            ${variantsHtml}
            <div class="qv-product__qty-row">
              <div class="qty-control">
                <button type="button" class="qty-control__btn" data-qv-qty="-1" aria-label="Decrease">−</button>
                <input type="number" name="quantity" value="1" min="1" class="qty-control__input" inputmode="numeric">
                <button type="button" class="qty-control__btn" data-qv-qty="1" aria-label="Increase">+</button>
              </div>
              <button type="submit" class="btn btn--primary" data-add-to-cart-btn>Add to Cart</button>
            </div>
            <a href="/products/${product.handle}" class="qv-product__view-full">View Full Details →</a>
          </form>
        </div>
      </div>
    `;

    this.#bindVariants(product);
    this.#bindQty();
    initAddToCart();

    // After successful add, switch from quick view to cart drawer
    const form = this.#body.querySelector('[data-qv-form]');
    form?.addEventListener('submit', () => {
      setTimeout(() => { this.close(); }, 350);
    });
  }

  #renderVariants(product) {
    const html = product.options.map((option, idx) => {
      const values = [...new Set(product.variants.map(v => v[`option${idx + 1}`]))].filter(Boolean);
      return `
        <div class="qv-option" data-qv-option="${idx + 1}">
          <span class="qv-option__label">${this.#esc(option)}</span>
          <div class="qv-option__values">
            ${values.map((v, i) => `
              <button type="button" class="qv-option__btn${i === 0 ? ' is-active' : ''}" data-qv-value="${this.#esc(v)}">${this.#esc(v)}</button>
            `).join('')}
          </div>
        </div>`;
    }).join('');
    return `${html}<input type="hidden" name="id" value="${product.variants[0].id}" data-qv-variant-id>`;
  }

  #bindVariants(product) {
    const optionGroups = this.#body.querySelectorAll('[data-qv-option]');
    if (!optionGroups.length) return;

    const updateVariant = () => {
      const selected = [...optionGroups].map(group => {
        return group.querySelector('.qv-option__btn.is-active')?.dataset.qvValue;
      });
      const variant = product.variants.find(v =>
        selected.every((val, i) => v[`option${i + 1}`] === val)
      );
      if (variant) {
        const input = this.#body.querySelector('[data-qv-variant-id]');
        if (input) input.value = variant.id;
        // Update price
        const onSale = variant.compare_at_price > variant.price;
        const priceEl = this.#body.querySelector('.qv-product__price');
        if (priceEl) {
          priceEl.innerHTML = onSale
            ? `<span class="qv-product__price--sale">${formatMoney(variant.price)}</span>
               <span class="qv-product__price--compare">${formatMoney(variant.compare_at_price)}</span>`
            : `<span>${formatMoney(variant.price)}</span>`;
        }
        // Disable button if unavailable
        const submitBtn = this.#body.querySelector('[data-add-to-cart-btn]');
        if (submitBtn) {
          submitBtn.disabled = !variant.available;
          submitBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }
      }
    };

    optionGroups.forEach(group => {
      group.querySelectorAll('.qv-option__btn').forEach(btn => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('.qv-option__btn').forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          updateVariant();
        });
      });
    });
  }

  #bindQty() {
    const input = this.#body.querySelector('input[name="quantity"]');
    if (!input) return;
    this.#body.querySelectorAll('[data-qv-qty]').forEach(btn => {
      btn.addEventListener('click', () => {
        const change = parseInt(btn.dataset.qvQty, 10);
        const current = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, current + change);
      });
    });
  }

  #esc(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  #truncate(html, len) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = (div.textContent || '').trim();
    return text.length > len ? text.slice(0, len) + '…' : text;
  }
}

/* Quick Add Modal — variant picker popup for multi-option products
   ============================================= */
class QuickAdd {
  #modal;
  #body;
  #isOpen = false;

  constructor() {
    this.#modal = document.getElementById('QuickAddModal');
    this.#body  = document.getElementById('QuickAddBody');
    if (!this.#modal) return;
    this.#init();
  }

  #init() {
    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-quick-add]');
      if (!trigger) return;
      e.preventDefault();
      const handle = trigger.dataset.productHandle;
      if (handle) this.open(handle);
    });

    for (const el of this.#modal.querySelectorAll('[data-quick-add-close]')) {
      el.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.#isOpen) this.close();
    });
  }

  async open(handle) {
    this.#isOpen = true;
    this.#modal.classList.add('is-open');
    this.#modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    this.#body.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;min-height:180px"><div class="spinner"></div></div>`;

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error();
      const product = await res.json();
      this.#render(product);
    } catch (e) {
      this.#body.innerHTML = `<p style="text-align:center;padding:2rem;color:var(--color-text-muted)">Unable to load product.</p>`;
    }
  }

  close() {
    this.#isOpen = false;
    this.#modal.classList.remove('is-open');
    this.#modal.setAttribute('aria-hidden', 'true');
    if (!window.cartDrawer?.isOpen && !window.quickView?.isOpen) {
      document.body.classList.remove('drawer-open');
    }
  }

  #render(product) {
    const onSale = product.compare_at_price > product.price;
    const firstVariant = product.variants[0];

    const thumb = product.featured_image
      ? product.featured_image + (product.featured_image.includes('?') ? '&' : '?') + 'width=160'
      : null;

    const priceHtml = onSale
      ? `<span class="qa-product__price--sale">${formatMoney(product.price)}</span>
         <span class="qa-product__price--compare">${formatMoney(product.compare_at_price)}</span>`
      : `<span>${formatMoney(product.price)}</span>`;

    const optionsHtml = product.options.map((option, idx) => {
      const values = [...new Set(product.variants.map(v => v[`option${idx + 1}`]))].filter(Boolean);
      const btns = values.map((v, i) =>
        `<button type="button" class="qa-option__btn${i === 0 ? ' is-active' : ''}" data-qa-value="${this.#esc(v)}">${this.#esc(v)}</button>`
      ).join('');
      return `<div class="qa-option" data-qa-option="${idx + 1}">
        <p class="qa-option__label">${this.#esc(option)}</p>
        <div class="qa-option__values">${btns}</div>
      </div>`;
    }).join('');

    this.#body.innerHTML = `
      <div class="qa-product">
        ${thumb
          ? `<img src="${thumb}" alt="${this.#esc(product.title)}" class="qa-product__thumb" width="72" height="72">`
          : `<div class="qa-product__thumb--placeholder"></div>`}
        <div class="qa-product__meta">
          ${product.vendor ? `<p class="qa-product__vendor">${this.#esc(product.vendor)}</p>` : ''}
          <h2 class="qa-product__title" id="QuickAddTitle">${this.#esc(product.title)}</h2>
          <div class="qa-product__price" data-qa-price>${priceHtml}</div>
        </div>
      </div>
      <div class="qa-divider"></div>
      <form data-add-to-cart data-qa-form>
        <div class="qa-options">${optionsHtml}</div>
        <input type="hidden" name="id" value="${firstVariant.id}" data-qa-variant-id>
        <input type="hidden" name="quantity" value="1">
        <div class="qa-actions">
          <button type="submit" class="btn btn--primary" data-add-to-cart-btn>
            <svg viewBox="0 0 24 24" aria-hidden="true" style="width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;flex-shrink:0"><path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"/></svg>
            Add to Cart
          </button>
          <a href="/products/${product.handle}" class="qa-view-full">View full details →</a>
        </div>
      </form>
    `;

    this.#bindOptions(product);
    initAddToCart();

    this.#body.querySelector('[data-qa-form]')?.addEventListener('submit', () => {
      setTimeout(() => this.close(), 400);
    });
  }

  #bindOptions(product) {
    const groups = this.#body.querySelectorAll('[data-qa-option]');
    if (!groups.length) return;

    const getSelected = () => [...groups].map(g =>
      g.querySelector('.qa-option__btn.is-active')?.dataset.qaValue
    );

    const update = () => {
      const selected = getSelected();

      // Mark unavailable buttons across all groups
      for (const [gIdx, group] of [...groups].entries()) {
        for (const btn of group.querySelectorAll('.qa-option__btn')) {
          const test = selected.map((v, i) => i === gIdx ? btn.dataset.qaValue : v);
          const exists = product.variants.some(v =>
            test.every((val, i) => v[`option${i + 1}`] === val) && v.available
          );
          btn.classList.toggle('is-unavailable', !exists);
        }
      }

      const variant = product.variants.find(v =>
        selected.every((val, i) => v[`option${i + 1}`] === val)
      );
      if (!variant) return;

      this.#body.querySelector('[data-qa-variant-id]').value = variant.id;

      const onSale = variant.compare_at_price > variant.price;
      const priceEl = this.#body.querySelector('[data-qa-price]');
      if (priceEl) {
        priceEl.innerHTML = onSale
          ? `<span class="qa-product__price--sale">${formatMoney(variant.price)}</span>
             <span class="qa-product__price--compare">${formatMoney(variant.compare_at_price)}</span>`
          : `<span>${formatMoney(variant.price)}</span>`;
      }

      const btn = this.#body.querySelector('[data-add-to-cart-btn]');
      if (btn) {
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }
    };

    for (const group of groups) {
      for (const btn of group.querySelectorAll('.qa-option__btn')) {
        btn.addEventListener('click', () => {
          for (const b of group.querySelectorAll('.qa-option__btn')) b.classList.remove('is-active');
          btn.classList.add('is-active');
          update();
        });
      }
    }

    update();
  }

  #esc(str) {
    if (!str) return '';
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }
}

/* Collection Page — filters, sort, search
   ============================================= */
class CollectionPage {
  #form;
  #sidebar;
  #overlay;
  #grid;
  #searchInput;
  #noResultsEl;
  #selectedVendors = new Set();

  constructor() {
    this.#form = document.querySelector('[data-collection-filter-form]');
    this.#sidebar = document.getElementById('CollectionFilters');
    this.#overlay = document.querySelector('[data-filters-overlay]');
    this.#grid = document.querySelector('[data-collection-grid]');
    this.#searchInput = document.querySelector('[data-collection-search-input]');
    this.#noResultsEl = document.querySelector('[data-collection-no-search-results]');

    if (!this.#grid && !this.#form) return;
    this.#init();
  }

  #init() {
    if (this.#form) {
      // Auto-submit when checkboxes change (exclude client-side vendor filter)
      this.#form.querySelectorAll('input[type="checkbox"]:not([data-vendor-filter])').forEach(input => {
        input.addEventListener('change', () => this.#form.submit());
      });

      // Debounced submit on price input change
      let priceTimer;
      this.#form.querySelectorAll('.filter-price__field input').forEach(input => {
        input.addEventListener('input', () => {
          clearTimeout(priceTimer);
          priceTimer = setTimeout(() => this.#form.submit(), 700);
        });
      });
    }

    // Sort pills
    document.querySelectorAll('[data-sort-pill]').forEach(btn => {
      btn.addEventListener('click', () => this.#applySort(btn.dataset.sortPill));
    });

    // Sort select
    const sortSelect = document.querySelector('[data-sort-select]');
    sortSelect?.addEventListener('change', () => this.#applySort(sortSelect.value));

    // Mobile filter drawer
    document.querySelector('[data-filters-open]')?.addEventListener('click', () => this.#openFilters());
    document.querySelector('[data-filters-close]')?.addEventListener('click', () => this.#closeFilters());
    this.#overlay?.addEventListener('click', () => this.#closeFilters());

    // Client-side product search
    if (this.#searchInput && this.#grid) {
      let searchTimer;
      this.#searchInput.addEventListener('input', () => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => this.#applyClientFilters(), 150);
      });
    }

    // Vendor checkboxes (client-side, current page only)
    for (const cb of document.querySelectorAll('[data-vendor-filter]')) {
      cb.addEventListener('change', () => {
        if (cb.checked) {
          this.#selectedVendors.add(cb.value);
        } else {
          this.#selectedVendors.delete(cb.value);
        }
        this.#applyClientFilters();
      });
    }

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.#sidebar?.classList.contains('is-open')) this.#closeFilters();
    });
  }

  #applySort(value) {
    if (!value) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  #openFilters() {
    this.#sidebar?.classList.add('is-open');
    this.#overlay?.classList.add('is-visible');
    document.body.classList.add('filters-open');
  }

  #closeFilters() {
    this.#sidebar?.classList.remove('is-open');
    this.#overlay?.classList.remove('is-visible');
    document.body.classList.remove('filters-open');
  }

  #applyClientFilters() {
    const q = (this.#searchInput?.value || '').trim().toLowerCase();
    const cells = this.#grid?.querySelectorAll('[data-product-cell]');
    if (!cells) return;
    let visibleCount = 0;

    for (const cell of cells) {
      const title = cell.dataset.productTitle || '';
      const vendor = cell.dataset.productVendor || '';
      const matchesSearch = q === '' || title.includes(q) || vendor.includes(q);
      const matchesVendor = this.#selectedVendors.size === 0 || this.#selectedVendors.has(vendor);
      cell.hidden = !(matchesSearch && matchesVendor);
      if (!cell.hidden) visibleCount++;
    }

    if (this.#noResultsEl) {
      this.#noResultsEl.hidden = visibleCount > 0;
    }
  }
}

/* Product Page — gallery, variant switching, qty stepper, add-to-cart
   ============================================= */
class ProductPage {
  #form;
  #gallery;
  #slidesTrack;
  #variants;
  #product;
  #currentSlide = 0;
  #zoomPane;
  #sectionId;
  #pickupFetchToken = 0;

  constructor() {
    if (!document.body.classList.contains('template-product')) return;

    const dataEl = document.querySelector('script[id^="ProductJson-"]');
    if (!dataEl) return;

    try {
      this.#product  = JSON.parse(dataEl.textContent);
      this.#variants = this.#product.variants;
    } catch (e) {
      return;
    }

    this.#sectionId = dataEl.id.replace('ProductJson-', '');

    this.#form    = document.querySelector('[data-product-form]');
    this.#gallery = document.querySelector('[data-product-gallery]');

    if (!this.#form) return;

    this.#initGallery();
    this.#initVariants();
    this.#initQuantity();
    this.#initSubmit();
    this.#initTabs();
    this.#initDescExpand();
  }

  /* Tabs (Description / Shipping & Returns / etc.) below the grid */
  #initTabs() {
    const tabsEl = document.querySelector('[data-product-tabs]');
    if (!tabsEl) return;

    const btns   = tabsEl.querySelectorAll('[data-tab]');
    const panels = tabsEl.querySelectorAll('[data-panel]');

    for (const btn of btns) {
      btn.addEventListener('click', () => {
        const idx = btn.dataset.tab;

        for (const b of btns) {
          b.classList.toggle('is-active', b.dataset.tab === idx);
          b.setAttribute('aria-selected', b.dataset.tab === idx ? 'true' : 'false');
        }

        /* CSS-only visibility — just toggle the class, no hidden attribute */
        for (const p of panels) {
          p.classList.toggle('is-active', p.dataset.panel === idx);
        }
      });
    }
  }

  /* Description expand / collapse */
  #initDescExpand() {
    const preview   = document.querySelector('[data-desc-preview]');
    const expandBtn = document.querySelector('[data-desc-expand]');
    if (!preview || !expandBtn) return;

    /* Hide the button if content is already short enough */
    if (preview.scrollHeight <= 140) {
      expandBtn.setAttribute('hidden', '');
      preview.classList.add('is-expanded'); /* remove gradient */
      return;
    }

    expandBtn.addEventListener('click', () => {
      const expanded = preview.classList.toggle('is-expanded');
      expandBtn.classList.toggle('is-expanded', expanded);
      expandBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      expandBtn.querySelector('span').textContent = expanded ? 'Show less' : 'See full description';
    });
  }

  /* Gallery — thumbnail clicks + arrows + swipe/drag scroll + hover zoom */
  #initGallery() {
    if (!this.#gallery) return;

    this.#slidesTrack = this.#gallery.querySelector('[data-gallery-slides]');

    for (const thumb of this.#gallery.querySelectorAll('[data-thumb-index]')) {
      thumb.addEventListener('click', () => this.#switchSlide(parseInt(thumb.dataset.thumbIndex)));
    }

    const slideCount = this.#gallery.querySelectorAll('[data-slide-index]').length;
    const prevBtn    = this.#gallery.querySelector('[data-gallery-prev]');
    const nextBtn    = this.#gallery.querySelector('[data-gallery-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.#switchSlide((this.#currentSlide - 1 + slideCount) % slideCount));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.#switchSlide((this.#currentSlide + 1) % slideCount));
    }

    /* Keep thumbnails/dots/zoom in sync when the shopper swipes or drag-scrolls the main image directly */
    if (this.#slidesTrack) {
      let scrollTimer;
      this.#slidesTrack.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const index = Math.round(this.#slidesTrack.scrollLeft / this.#slidesTrack.clientWidth);
          if (index !== this.#currentSlide) this.#switchSlide(index, false);
        }, 100);
      }, { passive: true });
    }

    this.#initZoom();
  }

  #switchSlide(index, scroll = true) {
    if (!this.#gallery) return;

    this.#currentSlide = index;

    if (scroll && this.#slidesTrack) {
      this.#slidesTrack.scrollTo({ left: this.#slidesTrack.clientWidth * index, behavior: 'smooth' });
    }

    for (const slide of this.#gallery.querySelectorAll('[data-slide-index]')) {
      slide.classList.toggle('is-active', parseInt(slide.dataset.slideIndex) === index);
    }

    const thumbs = this.#gallery.querySelectorAll('[data-thumb-index]');
    for (const thumb of thumbs) {
      const active = parseInt(thumb.dataset.thumbIndex) === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    }

    const dots = document.querySelectorAll('.product-gallery__dot');
    for (let i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', i === index);
    }

    this.#updateZoomImage();
  }

  /* Hover zoom — magnifies the active slide's image within the main frame */
  #initZoom() {
    const main = this.#gallery.querySelector('.product-gallery__main');
    this.#zoomPane = this.#gallery.querySelector('[data-zoom-pane]');
    if (!main || !this.#zoomPane) return;

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    main.addEventListener('mouseenter', () => {
      this.#updateZoomImage();
      this.#zoomPane.classList.add('is-active');
    });

    main.addEventListener('mousemove', (event) => {
      const rect = main.getBoundingClientRect();
      const xPct = ((event.clientX - rect.left) / rect.width) * 100;
      const yPct = ((event.clientY - rect.top) / rect.height) * 100;
      this.#zoomPane.style.backgroundPosition = `${xPct}% ${yPct}%`;
    });

    main.addEventListener('mouseleave', () => {
      this.#zoomPane.classList.remove('is-active');
    });
  }

  #updateZoomImage() {
    if (!this.#zoomPane) return;
    const activeSlide = this.#gallery.querySelector('[data-slide-index].is-active');
    const zoomSrc = activeSlide?.dataset.zoomSrc;
    if (!zoomSrc) return;
    this.#zoomPane.style.backgroundImage = `url("${zoomSrc}")`;
  }

  /* Variant selector — pill/swatch clicks */
  #initVariants() {
    for (const label of this.#form.querySelectorAll('[data-option-value]')) {
      label.addEventListener('click', () => {
        const optionIndex = parseInt(label.dataset.optionIndex);

        /* Mark selected within the same option group */
        for (const sibling of this.#form.querySelectorAll(`[data-option-index="${optionIndex}"]`)) {
          sibling.classList.remove('is-selected');
        }
        label.classList.add('is-selected');
        label.querySelector('input').checked = true;

        /* Update displayed selected value */
        const display = this.#form.querySelector(`[data-option-selected="${optionIndex}"]`);
        if (display) display.textContent = label.dataset.value;

        this.#updateVariant();
      });
    }
  }

  #updateVariant() {
    /* Collect selected option values in order */
    const options = [];
    const seen    = new Set();
    for (const el of this.#form.querySelectorAll('[data-option-index].is-selected')) {
      const idx = el.dataset.optionIndex;
      if (!seen.has(idx)) {
        seen.add(idx);
        options.push({ idx: parseInt(idx), value: el.dataset.value });
      }
    }
    options.sort((a, b) => a.idx - b.idx);
    const vals = options.map(o => o.value);

    /* Find matching variant */
    const variant = this.#variants.find(v =>
      vals.every((val, i) => v[`option${i + 1}`] === val)
    );
    if (!variant) return;

    /* Update hidden variant id */
    const idInput = this.#form.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    /* Update UI */
    this.#updatePrice(variant);
    this.#updateAvailability(variant);
    this.#updatePickupAvailability(variant);

    /* Switch gallery image to variant's featured image */
    if (variant.featured_image) {
      this.#switchSlide(variant.featured_image.position - 1);
    }

    /* Sync URL without page reload */
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  }

  #updatePrice(variant) {
    const container = document.querySelector('[data-product-price]');
    if (!container) return;

    const price        = variant.price;
    const comparePrice = variant.compare_at_price;
    const onSale       = comparePrice && comparePrice > price;

    if (onSale) {
      const pct = Math.round((comparePrice - price) * 100 / comparePrice);
      container.innerHTML = `
        <span class="product-info__price-sale" data-price>${formatMoney(price)}</span>
        <span class="product-info__price-compare" data-compare-price>${formatMoney(comparePrice)}</span>
        <span class="product-info__price-badge">Save ${pct}%</span>`;
    } else {
      container.innerHTML = `<span class="product-info__price-current" data-price>${formatMoney(price)}</span>`;
    }
  }

  #updateAvailability(variant) {
    const submit       = this.#form.querySelector('[data-product-submit]');
    const availability = this.#form.querySelector('[data-product-availability]');
    const skuEl        = document.querySelector('[data-sku]');

    if (submit) {
      submit.disabled = !variant.available;
      submit.innerHTML = variant.available
        ? `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"/></svg> Add to Cart`
        : 'Sold Out';
    }

    if (availability) {
      availability.innerHTML = variant.available
        ? `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg> In stock, ready to ship`
        : '';
    }

    if (skuEl && variant.sku) {
      skuEl.textContent = variant.sku;
      skuEl.closest('[data-sku-wrapper]')?.removeAttribute('hidden');
    }
  }

  /* Refetch pickup availability for the new variant via the Section Rendering API */
  async #updatePickupAvailability(variant) {
    const wrapper = document.querySelector('[data-pickup-availability-wrapper]');
    if (!wrapper || !this.#sectionId) return;

    const token = ++this.#pickupFetchToken;
    const url = `${window.location.pathname}?variant=${variant.id}&section_id=${this.#sectionId}`;

    try {
      const res  = await fetch(url);
      const html = await res.text();
      if (token !== this.#pickupFetchToken) return; /* a newer variant change superseded this one */

      const doc     = new DOMParser().parseFromString(html, 'text/html');
      const updated = doc.querySelector('[data-pickup-availability-wrapper]');
      if (updated) wrapper.innerHTML = updated.innerHTML;
    } catch (e) {
      /* leave existing content in place on network failure */
    }
  }

  /* Quantity stepper */
  #initQuantity() {
    const input    = this.#form.querySelector('.product-form__qty-input');
    const minusBtn = this.#form.querySelector('[data-qty-minus]');
    const plusBtn  = this.#form.querySelector('[data-qty-plus]');
    if (!input) return;

    minusBtn?.addEventListener('click', () => {
      const val = parseInt(input.value) || 1;
      if (val > 1) input.value = val - 1;
    });

    plusBtn?.addEventListener('click', () => {
      input.value = (parseInt(input.value) || 1) + 1;
    });
  }

  /* Add-to-cart form submission */
  #initSubmit() {
    this.#form.addEventListener('submit', async e => {
      e.preventDefault();

      const btn = this.#form.querySelector('[data-product-submit]');
      if (!btn || btn.disabled) return;

      const originalHTML = btn.innerHTML;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        const res = await fetch(window.cartRoutes.add, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.#form)
        });

        if (!res.ok) throw new Error('Cart add failed');

        /* Open cart drawer (CartDrawer listens to this event) */
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    });
  }
}

/* Newsletter Popup — delayed signup modal with dismiss/subscribe memory
   ============================================= */
class NewsletterPopup {
  #el;
  #dismissedKey = 'sswNewsletterPopupDismissedAt';
  #subscribedKey = 'sswNewsletterPopupSubscribed';

  constructor() {
    this.#el = document.querySelector('[data-newsletter-popup]');
    if (!this.#el) return;
    this.#init();
  }

  #init() {
    for (const el of this.#el.querySelectorAll('[data-newsletter-popup-close]')) {
      el.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.#el.classList.contains('is-open')) this.close();
    });

    const posted = this.#el.dataset.posted === 'true';

    if (posted) {
      localStorage.setItem(this.#subscribedKey, '1');
      this.open();
      return;
    }

    if (localStorage.getItem(this.#subscribedKey) === '1') return;

    const dismissedAt = parseInt(localStorage.getItem(this.#dismissedKey), 10);
    const frequencyDays = parseInt(this.#el.dataset.frequencyDays, 10) || 7;
    if (dismissedAt && Date.now() - dismissedAt < frequencyDays * 86400000) return;

    const delay = parseInt(this.#el.dataset.delay, 10) || 0;
    setTimeout(() => this.open(), delay);
  }

  open() {
    this.#el.classList.add('is-open');
    this.#el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
  }

  close() {
    this.#el.classList.remove('is-open');
    this.#el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    localStorage.setItem(this.#dismissedKey, String(Date.now()));
  }
}

/* Lazy Init on DOM ready
   ============================================= */
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
  window.searchPopup = new SearchPopup();
  window.quickView = new QuickView();
  window.quickAdd = new QuickAdd();
  new MobileNav();
  new CollectionPage();
  new ProductPage();
  new NewsletterPopup();
  initFaq();
  initAddToCart();
  initCurrencySelector();

  document.querySelectorAll('[data-hero-slider]').forEach(el => new HeroSlider(el));
  document.querySelectorAll('[data-product-slider]').forEach(el => new ProductSlider(el));
});
